import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { parseDate, CalendarDate, DateValue } from "@internationalized/date";
import {
  Container,
  FormSection,
  FormRow,
  FormColumn,
  ButtonWrapper,
  ConfigOptionsSection,
  PriceDisplay,
  DateTimeRow,
  ClearButton,
} from './styles';
import {
  BackArrow,
  TopProgress,
  Typography,
  Input,
  Select,
  Button,
  CheckBox,
  DatePickerCustom,
  Table,
} from "../../../../components";
import { billingService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";

interface ConfigurableOption {
  option: number;
  display?: string;
  option_value: string;
  option_value_id?: number;
  price: number;
  unit_price?: number;
  widget?: string;
}

interface Service {
  id: number;
  display_name: string;
  product: {
    id: number;
    name: string;
    cycles: Array<{
      id: number;
      display_name: string;
      currency: string;
    }>;
  };
  cycle: {
    id: number;
    display_name: string;
    currency: string;
  };
  is_free: boolean;
  notes: string;
  override_price?: number;
  renew_enabled: boolean;
  currency: {
    code: string;
  };
  configurable_options?: ConfigurableOption[];
  client?: {
    id: number;
    display?: string;
    name?: string;
  };
  billing_type?: string;
  status?: string;
  override_suspend_until?: string;
  current_service_cycle_end?: string;
}

const ServiceEditForm: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const { serviceId } = useParams();
  usePageTitle(t("EDIT_SERVICE"));

  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [products, setProducts] = useState<any[]>([]);
  const [configurableOptions, setConfigurableOptions] = useState<ConfigurableOption[]>([]);
  const [servicePriceDetails, setServicePriceDetails] = useState<any>(null);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedCycle, setSelectedCycle] = useState<number>(0);
  const [isFree, setIsFree] = useState(false);
  const [notes, setNotes] = useState('');
  const [overridePrice, setOverridePrice] = useState('');
  const [renewEnabled, setRenewEnabled] = useState(false);
  const [overrideSuspendUntil, setOverrideSuspendUntil] = useState<DateValue | null>(null);
  const [overrideSuspendUntilTime, setOverrideSuspendUntilTime] = useState('');
  const [currentServiceCycleEnd, setCurrentServiceCycleEnd] = useState<DateValue | null>(null);

  const getService = async () => {
    if (!serviceId) return;

    try {
      const res = await billingService.getService(serviceId);
      const serviceData = res.data;
      setService(serviceData);

      // Set form values
      setDisplayName(serviceData.display_name || '');
      setSelectedCycle(serviceData.cycle.id);
      setIsFree(serviceData.is_free || false);
      setNotes(serviceData.notes || '');
      setOverridePrice(serviceData.override_price ? serviceData.override_price.toString() : '');
      setRenewEnabled(serviceData.renew_enabled || false);

      // Parse dates
      if (serviceData.override_suspend_until) {
        const dateStr = serviceData.override_suspend_until.split('T')[0];
        const timeStr = new Date(serviceData.override_suspend_until).toLocaleTimeString('en-US', { hour12: false });
        setOverrideSuspendUntil(parseDate(dateStr));
        setOverrideSuspendUntilTime(timeStr);
      }

      if (serviceData.current_service_cycle_end) {
        const dateStr = serviceData.current_service_cycle_end.split('T')[0];
        setCurrentServiceCycleEnd(parseDate(dateStr));
      }

      // Set configurable options
      if (serviceData.configurable_options) {
        setConfigurableOptions(serviceData.configurable_options);
      }

      setPageLoading(false);
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || 'Error loading service'
      });
      navigate('/billing/services');
    }
  };

  const getProducts = async () => {
    try {
      const res = await billingService.getProducts();
      setProducts(res.data.objects || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const updatePrice = async () => {
    if (!service || service.billing_type !== 'prepaid') return;

    try {
      const res = await billingService.getServiceNewPrice(
        service.id,
        selectedCycle,
        overridePrice ? parseFloat(overridePrice) : null,
        configurableOptions,
        isFree
      );
      setServicePriceDetails(res.data);
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const handleSubmit = async () => {
    if (!service) return;

    setLoading(true);
    try {
      // Prepare date strings
      let overrideSuspendUntilStr = null;
      if (overrideSuspendUntil) {
        const dateStr = `${overrideSuspendUntil.year}-${String(overrideSuspendUntil.month).padStart(2, '0')}-${String(overrideSuspendUntil.day).padStart(2, '0')}`;
        if (overrideSuspendUntilTime) {
          overrideSuspendUntilStr = `${dateStr}T${overrideSuspendUntilTime}`;
        } else {
          overrideSuspendUntilStr = dateStr;
        }
      }

      let currentServiceCycleEndStr = null;
      if (currentServiceCycleEnd) {
        currentServiceCycleEndStr = `${currentServiceCycleEnd.year}-${String(currentServiceCycleEnd.month).padStart(2, '0')}-${String(currentServiceCycleEnd.day).padStart(2, '0')}`;
      }

      const data: any = {
        display_name: displayName,
        product: selectedProduct?.id || service.product.id,
        cycle: selectedCycle,
        is_free: isFree,
        notes: notes,
        override_price: overridePrice ? parseFloat(overridePrice) : null,
        renew_enabled: renewEnabled,
        override_suspend_until: overrideSuspendUntilStr,
        current_service_cycle_end: currentServiceCycleEndStr,
        configurable_options: configurableOptions,
        status: service.status,
        // Preserve required fields from original service
        price_override_tiers: service.price_override_tiers || [],
      };

      await billingService.updateService(service.id, data);

      viewAlert({
        severity: 'success',
        message: t('SERVICE_UPDATED_SUCCESSFULLY')
      });

      navigate(`/billing/services/${service.id}`);
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_UPDATING_SERVICE')
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getService();
    getProducts();
  }, [serviceId]);

  useEffect(() => {
    if (service && products.length > 0) {
      const product = products.find(p => p.id === service.product.id);
      if (product) {
        setSelectedProduct(product);
      }
    }
  }, [service, products]);

  useEffect(() => {
    if (service && selectedCycle) {
      updatePrice();
    }
  }, [selectedCycle, overridePrice, configurableOptions, isFree]);

  useEffect(() => {
    if (selectedProduct && selectedProduct.cycles) {
      const currentCycleExists = selectedProduct.cycles.find((c: any) => c.id === selectedCycle);
      if (!currentCycleExists && selectedProduct.cycles.length > 0) {
        setSelectedCycle(selectedProduct.cycles[0].id);
      }
    }
  }, [selectedProduct]);

  if (pageLoading) {
    return (
      <Container>
        <TopProgress loading={pageLoading} />
        <Typography variant="h1">Loading...</Typography>
      </Container>
    );
  }

  if (!service) return null;

  // Prepare configurable options data for table
  const configOptionsColumns = [
    { title: t("OPTION"), key: 'option' },
    { title: t("OPTION_VALUE"), key: 'option_value' },
    { title: t("PRICE"), key: 'price' },
    { title: '', key: 'actions' },
  ];

  const configOptionsRows = configurableOptions.map((opt) => ({
    option: opt.display || opt.option,
    option_value: opt.option_value || '-',
    price: `${opt.price} ${service?.currency?.code || ''}`,
    id: opt.option,
  }));

  return (
    <Container>
      <BackArrow fallbackPath={`/billing/services/${serviceId}`} />

      <Typography variant="h1">
        {t("EDIT_SERVICE")}: {service?.product?.name || t("SERVICE")}
      </Typography>

      {service?.client && (
        <Typography variant="body" customStyles={{ marginBottom: '20px' }}>
          {t("CLIENT")}: <a href={`/clients-users/clients/${service.client.id}`}>
            {service.client.display || service.client.name}
          </a>
        </Typography>
      )}

      <div>
        <FormSection>
          <FormRow>
            <FormColumn>
              <Input
                title={t("DISPLAY_NAME")}
                values={displayName}
                handleChange={setDisplayName}
                placeholder={t("ENTER_DISPLAY_NAME")}
                required
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Select
                title={t("PRODUCT")}
                data={products.map(product => ({
                  id: product.id,
                  title: product.name,
                }))}
                selectedValue={selectedProduct?.id}
                onChange={(selected) => {
                  const product = products.find(p => p.id === selected.id);
                  setSelectedProduct(product);
                  if (product?.cycles?.length > 0) {
                    setSelectedCycle(product.cycles[0].id);
                  }
                }}
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Select
                title={t("BILLING_CYCLE")}
                data={selectedProduct?.cycles?.map((cycle: any) => ({
                  id: cycle.id,
                  title: `${cycle.display_name} (${cycle.currency})`,
                })) || []}
                selectedValue={selectedCycle}
                onChange={(selected) => setSelectedCycle(selected.id)}
                disabled={!selectedProduct?.cycles?.length}
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <CheckBox
                title={t("IS_FREE")}
                value={isFree}
                onChange={() => setIsFree(!isFree)}
              />
            </FormColumn>
          </FormRow>

          {!isFree && (
            <FormRow>
              <FormColumn>
                <Input
                  title={t("OVERRIDE_PRICE")}
                  values={overridePrice}
                  handleChange={setOverridePrice}
                  placeholder={t("ENTER_OVERRIDE_PRICE")}
                  type="number"
                />
                <Typography variant="small" customStyles={{ marginTop: '5px', color: '#666' }}>
                  {t("OVERRIDE_PRICE_HINT")}
                </Typography>
              </FormColumn>
            </FormRow>
          )}

          <FormRow>
            <FormColumn>
              <CheckBox
                title={t("RENEW_ENABLED")}
                value={renewEnabled}
                onChange={() => setRenewEnabled(!renewEnabled)}
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <Input
                title={t("NOTES")}
                values={notes}
                handleChange={setNotes}
                placeholder={t("ENTER_NOTES")}
                isTextArea
              />
            </FormColumn>
          </FormRow>

          <DateTimeRow>
            <FormColumn style={{ flex: 1 }}>
              <DatePickerCustom
                title={String(t("DO_NOT_SUSPEND_UNTIL"))}
                value={overrideSuspendUntil}
                onChange={(val: DateValue | null) => setOverrideSuspendUntil(val)}
              />
            </FormColumn>
            <FormColumn style={{ flex: 0.5 }}>
              <Input
                title={t("TIME")}
                values={overrideSuspendUntilTime}
                handleChange={setOverrideSuspendUntilTime}
                placeholder="HH:MM:SS"
              />
            </FormColumn>
            <ClearButton
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setOverrideSuspendUntil(null);
                setOverrideSuspendUntilTime('');
              }}
            >
              {t("CLEAR")}
            </ClearButton>
          </DateTimeRow>

          <DateTimeRow>
            <FormColumn style={{ flex: 1 }}>
              <DatePickerCustom
                title={String(t("CURRENT_CYCLE_END"))}
                value={currentServiceCycleEnd}
                onChange={(val: DateValue | null) => setCurrentServiceCycleEnd(val)}
              />
            </FormColumn>
            <ClearButton
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setCurrentServiceCycleEnd(null);
              }}
            >
              {t("CLEAR")}
            </ClearButton>
          </DateTimeRow>
        </FormSection>

        <ConfigOptionsSection>
          <Typography variant="h2">{t("CONFIGURABLE_OPTIONS")}</Typography>
          {configurableOptions.length > 0 ? (
            <Table
              columns={configOptionsColumns}
              rows={configOptionsRows}
              hideCheckbox
            />
          ) : (
            <Typography variant="body">{t("NO_CONFIGURABLE_OPTIONS")}</Typography>
          )}
        </ConfigOptionsSection>

        {servicePriceDetails && servicePriceDetails.price !== null && (
          <PriceDisplay>
            {!servicePriceDetails.free_of_charge ? (
              <Typography variant="body">
                {t("SERVICE_PRICE_AFTER_SAVING")}: {servicePriceDetails.price} {service?.currency?.code}
              </Typography>
            ) : (
              <Typography variant="body">
                {t("SERVICE_PRICE_AFTER_SAVING")}: <del>{servicePriceDetails.price}</del> 0 {service?.currency?.code}
              </Typography>
            )}
          </PriceDisplay>
        )}

        <ButtonWrapper>
          <Button
            variant="secondary"
            size="display"
            onClick={() => navigate(`/billing/services/${serviceId}`)}
            disabled={loading}
            title={t("CANCEL")}
          />
          <Button
            variant="primary"
            size="display"
            onClick={handleSubmit}
            loading={loading}
            title={t("SAVE_SERVICE")}
          />
        </ButtonWrapper>
      </div>
    </Container>
  );
};

export default ServiceEditForm;




